import React from 'react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { Button, Callout, EmptyState, Skeleton, Text } from '@/nova/components';
import { useAppDispatch, useAppSelector } from '@/store';
import { selectStudioPeriod, setStudioPeriod } from '@/store/slices/studioPeriod';
import { homePeriodOfReport, reportPeriodFor } from '@/nova/pages/home/shared-period';
import { useLandingPageViews, viewsKey } from '@/nova/pages/website-analytics/useLandingPageViews';
import { useWebsiteTrafficQualityGate } from '@/nova/pages/website-analytics/quality/gate';
import { Sheet, useSheetDismiss } from '@/nova/components/Sheet';
import { PeriodSelector, type PresetId } from '@/nova/pages/affiliates/components/ReportsTab/PeriodSelector';
import {
  formatCountValue,
  formatMoneyValue,
  rangeFromPreset,
  type DateRange,
} from '@/nova/pages/affiliates/reports-helpers';
import { toNumber, type Metrics } from '@/nova/pages/home/stats-shape';

import { useFeatureGate } from '@/nova/hooks/useFeatureGate';
import {
  linkAssetName,
  linkAssetPath,
  playersFromCampaignPath,
  playersFromLandingPagePath,
} from '../../link-attribution';
import { assetKindOpens } from '../AssetComposerOverlay/asset-overlay';
import { versionStamp } from '../../tracking-link';
import type { TrackingLink, TrackingLinkKind } from '../../tracking-links-api';
import { useAssetStats } from '../../useAssetStats';
import {
  STATS_PERIOD_PARAM,
  formatStatsPeriod,
  parseStatsPeriod,
  replaceSearchParams,
  type StatsPeriod,
} from '../../useSheetParam';
import { VIDEO_STAT_DEFS, statRatio, sumMetricField, videoStatValue } from '../../video-stats';
import { isOwnLink, openInApp, trackingKindLabel } from '../LandingPageComposer/link-for';
import { AssetLinksFooter } from './AssetLinksFooter';
import * as S from './AssetStatsSheet.styles';

export interface AssetStatsSheetProps {
  /** What the asset is (its links' kind). */
  kind: TrackingLinkKind;
  /** The asset's spark creatives id (a landing page: its spark id). */
  creativeId: string;
  /** The asset's title, shown in the header (so the owner knows which asset these are) and the caption. */
  name?: string;
  /** Close the sheet (backdrop / Escape / done). */
  onClose: () => void;
  /** A landing page's published path (`/welcome-bonus`): its Views are read for it. Absent: not published yet. */
  landingPath?: string;
}

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * The analytics period of a day range: its first day's start to the end of its last day, in UTC. Not clipped to now, so
 * the quality read gets the sheet's own last date back (the views read stops at now). Pure.
 */
const daySpan = (from: string, to: string): { from: number; to: number } | null => {
  const start = Date.parse(`${from}T00:00:00Z`);
  const end = Date.parse(`${to}T00:00:00Z`);
  if (Number.isNaN(start) || Number.isNaN(end)) return null;
  return { from: start, to: end + DAY_MS };
};

/** Fixed currency: the stats service money fields are single-currency per brand and rendered as USD, matching
 *  the Home / Stats / Campaign pages (they assume one currency and prefix `$`). */
const CURRENCY = 'USD';

/** One breakdown row: a link and its figures over the shown period. */
interface LinkRow {
  link: TrackingLink;
  registrations: number;
  ftds: number;
  deposits: number;
}

/** The asset's links with their figures, for the breakdown: a landing page's own link first then the most
 *  registrations; any other asset's newest version first (the version picker's order). Pure. */
const linkRows = (
  links: readonly TrackingLink[],
  byCampaign: ReadonlyMap<string, Metrics>,
  kind: TrackingLinkKind,
  creativeId: string,
): LinkRow[] => {
  const rows = links.map((link): LinkRow => {
    const m = byCampaign.get(link.campaign) ?? {};
    return {
      link,
      registrations: toNumber(m.registrations),
      ftds: toNumber(m.ftds),
      deposits: toNumber(m.deposits),
    };
  });
  if (kind === 'landing_page') {
    const own = (r: LinkRow): number => (isOwnLink(r.link, creativeId) ? 1 : 0);
    return rows.sort((a, b) => own(b) - own(a) || b.registrations - a.registrations);
  }
  return rows.sort((a, b) => b.link.version_key.localeCompare(a.link.version_key, undefined, { numeric: true }));
};

/** The human label for each KPI (literal `t()` calls so the extractor harvests them). */
const statLabel = (id: string, t: TFunction): string => {
  switch (id) {
    case 'registrations':
      return t('Registrations');
    case 'ftds':
      return t('FTDs');
    case 'deposits':
      return t('Deposits');
    case 'deposit_amount':
      return t('Deposit volume');
    case 'ftd_value':
      return t('FTD value');
    case 'ggr':
      return t('GGR');
    default:
      return id;
  }
};

/** The sheet's title and the name of an untitled asset, per kind (literal `t()` calls). */
const kindCopy = (kind: TrackingLinkKind, t: TFunction): { title: string; untitled: string } => {
  switch (kind) {
    case 'video':
      return { title: t('Video ad performance'), untitled: t('Untitled video ad') };
    case 'kit':
      return { title: t('Image banner performance'), untitled: t('Untitled image banner') };
    case 'playable':
      return { title: t('Mini-game performance'), untitled: t('Untitled mini-game') };
    case 'banner':
      return { title: t('HTML5 banner performance'), untitled: t('Untitled HTML5 banner') };
    default:
      return { title: t('Landing page performance'), untitled: t('Untitled landing page') };
  }
};

/**
 * A marketing asset's "Stats" sheet (video ad, image banner, mini-game, HTML5 banner, landing page), opened from its
 * composer's header pill or its library card's figures. Shows the performance of the asset's tracking links (one per
 * version; a landing page: every link landing on it, its own included) from the Owners Hub Stats aggregation API over
 * a selectable period (default 30 days): the KPI tiles summed across the links, a per-version (per-link) breakdown when
 * there are several, and the way to the players they brought; its footer holds the links themselves (copyable, and
 * where each goes), except on a landing page, whose "By link" table already lists them. Read-only; states cover no brand, no tracking link yet, a load error, and the numbers themselves.
 */
export const AssetStatsSheet = ({
  kind,
  creativeId,
  name,
  onClose,
  landingPath,
}: AssetStatsSheetProps): React.ReactElement => {
  const { t } = useTranslation();
  const { closeHandle } = useSheetDismiss(onClose);
  const copy = kindCopy(kind, t);
  const displayName = name?.trim() || copy.untitled;
  const landing = kind === 'landing_page';

  // The period lives in the address (`stats_period`) beside the sheet's own key, so coming Back from one of the sheet's
  // deep links shows the same period. Changing it rewrites the current entry (no history entry per pick). With none in
  // the address it is the range the owner last picked on any page with one (as closely as this picker can show it),
  // else the default; a pick here becomes that range for every page when Home can show it too.
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const studioPeriod = useAppSelector(selectStudioPeriod);
  const periodRaw = new URLSearchParams(location.search).get(STATS_PERIOD_PARAM);
  const { preset, range } = React.useMemo(
    () =>
      periodRaw === null && studioPeriod !== null
        ? reportPeriodFor(studioPeriod)
        : parseStatsPeriod(periodRaw, new Date()),
    [periodRaw, studioPeriod],
  );
  const setPeriod = (next: StatsPeriod): void => {
    replaceSearchParams(navigate, location, { [STATS_PERIOD_PARAM]: formatStatsPeriod(next) });
    const shared = homePeriodOfReport(next);
    if (shared !== null) dispatch(setStudioPeriod(shared));
  };
  const onPreset = (next: PresetId): void => setPeriod({ preset: next, range: rangeFromPreset(next, new Date()) });
  const onCustom = (next: DateRange): void => setPeriod({ preset: 'custom', range: next });

  // A landing page's Views over the same period, from the analytics service (its page views on the brand's landing
  // addresses).
  const tenantRaw = useAppSelector((state) => Number(state.casinoInstance.displayBrand?.tenant_id));
  const tenantId = Number.isFinite(tenantRaw) && tenantRaw > 0 ? tenantRaw : undefined;
  const viewsSpan = React.useMemo(() => daySpan(range.from, range.to), [range.from, range.to]);
  const traffic = useWebsiteTrafficQualityGate(tenantId);
  const views = useLandingPageViews(tenantId, landing && landingPath ? [landingPath] : [], landing ? viewsSpan : null, {
    enabled: traffic.websiteAnalytics,
    quality: traffic.quality,
    asset: true,
  });

  const { status, links, linksStatus, metricsList, byCampaign, error, reload } = useAssetStats({
    kind,
    creativeId,
    range,
  });
  const rows = React.useMemo(
    () => linkRows(links, byCampaign, kind, creativeId),
    [links, byCampaign, kind, creativeId],
  );

  // Leaving for the Players page (or an asset) keeps the sheet's entry as it is: the sheet is the address's
  // (`?stats=`), so the new page drops it, and Back comes back to it open.
  // An asset row links to its asset only where that kind's pages open for this owner (both of their route's gates).
  const gate = useFeatureGate();
  const openPlayers = (e: React.MouseEvent<HTMLAnchorElement>, path: string): void => openInApp(e, path, navigate);

  const regs = sumMetricField(metricsList, 'registrations');
  const ftds = sumMetricField(metricsList, 'ftds');
  const regToFtd = statRatio(ftds, regs);

  // A landing page's Views: its page views over the period (a dash and why when they cannot be read). Shown whatever
  // the links say: a published page has views before any tracking link lands on it, or when the links cannot be read.
  const viewsCard =
    landing && traffic.websiteAnalytics ? (
      <S.Card>
        <S.CardLabel text="Views" />
        {!landingPath ? (
          <>
            <S.CardValue raw text="–" />
            <S.CardNote text="Not published yet" />
          </>
        ) : views.status === 'ready' && views.byPath.has(viewsKey(landingPath)) ? (
          <S.CardValue raw text={formatCountValue(views.byPath.get(viewsKey(landingPath)) ?? 0)} />
        ) : views.status === 'dash' ? (
          <>
            <S.CardValue raw text="–" />
            {views.note ? (
              <S.CardNote text={views.note} {...(views.n === undefined ? {} : { placeholders: { n: views.n } })} />
            ) : null}
          </>
        ) : views.status === 'unavailable' || views.status === 'ready' ? (
          <>
            <S.CardValue raw text="–" />
            <S.CardNote text="Not available right now" />
          </>
        ) : (
          <Skeleton width={48} height={26} radius={6} />
        )}
      </S.Card>
    ) : null;
  /** A notice in place of the link figures, under the page's Views when it is a landing page. */
  const withViews = (notice: React.ReactNode): React.ReactNode =>
    viewsCard ? (
      <>
        <S.Grid>{viewsCard}</S.Grid>
        {notice}
      </>
    ) : (
      notice
    );

  const body = ((): React.ReactNode => {
    if (status === 'no-brand') {
      return <Callout tone="warning" title={t('No brand selected')} text={t('Select a brand to see its stats.')} />;
    }
    if (status === 'no-campaigns') {
      return withViews(
        landing ? (
          <EmptyState
            text={t(
              "No tracking link lands here yet. Get the page's tracking link, or point an ad's link here, to see who it brings in.",
            )}
          />
        ) : (
          <EmptyState text={t('No tracking link yet. Get a tracking link to see who it brings in.')} />
        ),
      );
    }
    if (status === 'error') {
      return withViews(
        <Callout
          tone="danger"
          title={t('Could not load stats')}
          text={error || t('Please try again.')}
          action={<Button variant="outline" label={t('Try again')} onClick={reload} />}
        />,
      );
    }
    if (status === 'loading') {
      return (
        <S.Grid>
          {Array.from({ length: VIDEO_STAT_DEFS.length + (viewsCard ? 2 : 1) }).map((_v, i) => (
            <S.SkeletonCard key={i} aria-hidden="true" />
          ))}
        </S.Grid>
      );
    }
    // status === 'ok'
    return (
      <S.Grid>
        {viewsCard}
        {VIDEO_STAT_DEFS.map((def) => {
          const value = videoStatValue(metricsList, def);
          const display = def.kind === 'money' ? formatMoneyValue(value, CURRENCY) : formatCountValue(value);
          return (
            <S.Card key={def.id}>
              <S.CardLabel raw text={statLabel(def.id, t)} />
              <S.CardValue raw text={display} />
            </S.Card>
          );
        })}
        <S.Card>
          <S.CardLabel text="Reg → FTD" />
          <S.CardValue raw text={regToFtd == null ? '–' : `${(regToFtd * 100).toFixed(1)}%`} />
        </S.Card>
      </S.Grid>
    );
  })();

  // Several links: each one's figures, and its players once someone registered through it (no dead ends).
  const breakdown = ((): React.ReactNode => {
    if (status !== 'ok' || rows.length < 2) return null;
    return (
      <S.Breakdown>
        {landing ? <S.BreakdownTitle text="By link" /> : <S.BreakdownTitle text="By version" />}
        <S.Table>
          <thead>
            <tr>
              <S.HeadCell scope="col">{landing ? <Text text="Link" /> : <Text text="Version" />}</S.HeadCell>
              <S.HeadCell scope="col" $num>
                <Text text="Reg" />
              </S.HeadCell>
              <S.HeadCell scope="col" $num>
                <Text text="FTD" />
              </S.HeadCell>
              <S.HeadCell scope="col" $num>
                <Text text="Deposits" />
              </S.HeadCell>
              <S.HeadCell scope="col">
                <span aria-hidden="true" />
              </S.HeadCell>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const { link } = row;
              const path = playersFromCampaignPath(link.campaign);
              const own = landing && isOwnLink(link, creativeId);
              const label = landing
                ? own
                  ? t("This page's own link")
                  : linkAssetName(link)
                : versionStamp(link.version_key) || link.name;
              return (
                <S.Row key={link.id}>
                  <S.LabelCell title={landing && !own ? `${trackingKindLabel(link.kind, t)} · ${label}` : link.name}>
                    {landing && !own ? <S.RowKind raw text={trackingKindLabel(link.kind, t)} /> : null}
                    {landing && !own && link.creative_id && assetKindOpens(link.kind, gate.isEnabled) ? (
                      <S.AssetLink href={linkAssetPath(link)} onClick={(e) => openPlayers(e, linkAssetPath(link))}>
                        <Text raw text={label} tag="span" />
                      </S.AssetLink>
                    ) : (
                      <Text raw text={label} />
                    )}
                  </S.LabelCell>
                  <S.Cell $num>
                    <Text raw text={formatCountValue(row.registrations)} />
                  </S.Cell>
                  <S.Cell $num>
                    <Text raw text={formatCountValue(row.ftds)} />
                  </S.Cell>
                  <S.Cell $num>
                    <Text raw text={formatCountValue(row.deposits)} />
                  </S.Cell>
                  <S.Cell>
                    {row.registrations > 0 ? (
                      <S.PlayersLink href={path} onClick={(e) => openPlayers(e, path)}>
                        <Text text="See players" tag="span" />
                      </S.PlayersLink>
                    ) : null}
                  </S.Cell>
                </S.Row>
              );
            })}
          </tbody>
        </S.Table>
      </S.Breakdown>
    );
  })();

  // Everyone the asset brought, once someone registered: a landing page's are everyone through it (any link landing
  // there); another asset's single link leads straight to its players (several: the breakdown's rows do).
  const playersRow = ((): React.ReactNode => {
    if (status !== 'ok' || regs <= 0) return null;
    const path = landing
      ? playersFromLandingPagePath(creativeId)
      : rows.length === 1
        ? playersFromCampaignPath(rows[0].link.campaign)
        : '';
    if (!path) return null;
    return (
      <S.PlayersRow>
        <S.PlayersLink href={path} onClick={(e) => openPlayers(e, path)}>
          <Text text="See players" tag="span" />
        </S.PlayersLink>
      </S.PlayersRow>
    );
  })();

  return (
    <Sheet
      header={
        <S.HeaderText>
          <S.HeaderTitle raw text={copy.title} />
          <S.HeaderName raw text={displayName} />
        </S.HeaderText>
      }
      ariaLabel={copy.title}
      onClose={onClose}
      closeHandle={closeHandle}
      maxWidth="720px"
      footer={
        // A landing page's sheet lists its links in the "By link" table already: the footer would only repeat them.
        landing ? undefined : (
          <AssetLinksFooter
            kind={kind}
            creativeId={creativeId}
            links={rows.map((row) => row.link)}
            linksStatus={linksStatus}
          />
        )
      }
    >
      <S.Body>
        <S.Controls>
          <PeriodSelector preset={preset} range={range} onPreset={onPreset} onCustom={onCustom} />
        </S.Controls>
        {status === 'ok' ? (
          <S.Caption
            text="Across {{n}} tracking link(s) for {{name}}."
            placeholders={{ n: links.length, name: displayName }}
          />
        ) : null}
        {body}
        {playersRow}
        {breakdown}
      </S.Body>
    </Sheet>
  );
};

export default AssetStatsSheet;
